/**
 * @fileoverview Provides functions for applying code changes to files using AI models, including `applyProposedChangesToFiles`, `getRewriteWithGpt4oMini`, `applyProposedChangesToFilesWithModel`, `getRewriteViaSpeculativeDecodingFireworks`, and `applyCodeReplacements`. It generates `RewriteWithPath` objects and leverages speculative decoding with models like `Gpt4oMiniModel` and `Llama_33_70B_n1_multiLine_Model`. It uses `CodeBlock` and `CodeReplacement` types.
 */

import {
  applyMultipleChangesPrompt,
  getApplyChangesPrompt,
} from '@iterator/shared-common/src/apply_changes_prompt'
import { Model } from '@iterator/shared-node/ai/llm_types'
import { debug_block, error_block } from '@iterator/shared-node/util/logging'
import { time } from '@iterator/shared-node/util/timing'
import { trackUsage } from '@iterator/shared-node/util/usage_tracker'
import { existsSync, readFileSync } from 'fs'
import { Gpt4oMiniModel, Llama_33_70B_n1_multiLine_Model } from './ai/models'
import { AiStreamer } from './ai_streamer'
import { CodeBlock } from './generate/generate_v2_prompt'
import { CodeReplacement } from './generate/generate_v3_prompt'
import { ProposedChange } from './parse_stream'
import { applyRewritesToFiles, diffChangesToRewrites, RewriteWithPath } from './rewrite'
import Diff = require('diff')

export async function applyProposedChangesToFiles(
  proposedChanges: ProposedChange[],
): Promise<RewriteWithPath[]> {
  const rewrites: Promise<RewriteWithPath[]>[] = []

  for (const change of proposedChanges) {
    if (existsSync(change.path)) {
      const originalFileContents = readFileSync(change.path, 'utf8')
      rewrites.push(
        getRewriteWithGpt4oMini(change, originalFileContents, proposedChanges.length > 1),
      )
    } else {
      rewrites.push(
        Promise.resolve([
          {
            filePath: change.path,
            type: 'createFile',
            newLines: change.text.split('\n'),
            newLineRange: { start: 0, end: change.text.split('\n').length },
            originalLineRange: { start: 0, end: 0 },
            originalLines: [],
          },
        ]),
      )
    }
  }

  applyRewritesToFiles((await Promise.all(rewrites)).flat())
  return (await Promise.all(rewrites)).flat()
}

/**
 * Use speculative decoding to apply proposed changes to a file.
 * Breaks up the changes into multiple rewrites if this works.
 */
export async function getRewriteWithGpt4oMini(
  change: ProposedChange,
  originalFileContents: string,
  quiet: boolean = false,
): Promise<RewriteWithPath[]> {
  const gpt4oApi = Gpt4oMiniModel.getApi()

  const streamer = new AiStreamer()
  const { message, usage, timing } = await time('applyChangesViaSpeculativeDecoding', () =>
    gpt4oApi.streamChatCompletions(
      `You are a coding assistant that rewrites code.

You are given a <file> and a <proposed_change> to that file.

Output a single rewrite of the entire file.

Make no changes to the file apart from the proposed changes. 

Do not include any other text than the rewritten file.
`,
      [`<file>\n${originalFileContents}\n</file>`],
      [
        {
          role: 'user',
          content: `Please apply the following <proposed_change> to the <file>\n\n<proposed_change>\n${change.text}\n</proposed_change>. Output the entire file. Do not output it in a markdown code block. If there are comments guiding the change, do not include those in the output.`,
        },
      ],
      (chunk) => {
        if (!quiet) {
          streamer.stream(chunk)
        }
      },
      originalFileContents,
    ),
  )
  let content = parseFromPossibleBackticks(message.content)
  debug_block('New file contents', content)
  debug_block('applyChangesViaSpeculativeDecoding', { usage, timing })

  // Special case newline at end of file since the model may not include it.
  if (originalFileContents.endsWith('\n') && !content.endsWith('\n')) {
    content += '\n'
  }

  const changes = Diff.diffLines(originalFileContents, content)
  const rewrites = diffChangesToRewrites(changes)

  return rewrites.map((rewrite) => ({
    ...rewrite,
    type: 'editFile',
    filePath: change.path,
  }))
}
function parseFromPossibleBackticks(content: string): string {
  // Remove markdown code block backticks if present
  const match = content.match(/^```(?:\w+)?\n([\s\S]*?)```$/m)
  const result = match ? match[1]! : content
  return result.trim()
}

export async function applyProposedChangesToFilesWithModel(
  codeBlocks: CodeBlock[],
  model: Model = Llama_33_70B_n1_multiLine_Model,
): Promise<RewriteWithPath[]> {
  const rewrites: Promise<RewriteWithPath[]>[] = []

  const pathToEditCodeBlocks = new Map<string, CodeBlock[]>()
  for (const change of codeBlocks) {
    if (existsSync(change.path) && change.type !== 'entire_file') {
      const existing = pathToEditCodeBlocks.get(change.path) || []
      existing.push(change)
      pathToEditCodeBlocks.set(change.path, existing)
    } else if (existsSync(change.path) && change.type === 'entire_file') {
      const originalFileContents = readFileSync(change.path, 'utf8')
      const originalLines = originalFileContents.split('\n')
      rewrites.push(
        Promise.resolve([
          {
            filePath: change.path,
            type: 'editFile',
            newLines: change.code.split('\n'),
            newLineRange: { start: 0, end: change.code.split('\n').length },
            originalLineRange: { start: 0, end: originalLines.length },
            originalLines: originalLines,
          },
        ]),
      )
    } else {
      rewrites.push(
        Promise.resolve([
          {
            filePath: change.path,
            type: 'createFile',
            newLines: change.code.split('\n'),
            newLineRange: { start: 0, end: change.code.split('\n').length },
            originalLineRange: { start: 0, end: 0 },
            originalLines: [],
          },
        ]),
      )
    }
  }

  for (const [path, codeBlocks] of Array.from(pathToEditCodeBlocks.entries())) {
    const originalFileContents = readFileSync(path, 'utf8')
    const fileRewrites = getRewriteViaSpeculativeDecodingFireworks(
      codeBlocks,
      originalFileContents,
      model,
    )
    rewrites.push(fileRewrites)
  }

  applyRewritesToFiles((await Promise.all(rewrites)).flat())
  return (await Promise.all(rewrites)).flat()
}

/**
 * Use speculative decoding to apply proposed changes to a file.
 * Breaks up the changes into multiple rewrites if this works.
 */
export async function getRewriteViaSpeculativeDecodingFireworks(
  changes: CodeBlock | CodeBlock[],
  originalFileContents: string,
  model: Model,
): Promise<RewriteWithPath[]> {
  changes = Array.isArray(changes) ? changes : [changes]
  const firstChange = changes[0]!

  if (!changes.every((change) => change.path === firstChange.path)) {
    throw new Error('All changes must have the same path when using speculative decoding')
  }

  const originalFileCodeBlock = `\`\`\`${firstChange.language}\n${originalFileContents}\n\`\`\``
  const messages =
    changes.length === 1
      ? getApplyChangesPrompt(originalFileContents, changes[0]!)
      : applyMultipleChangesPrompt(originalFileContents, changes)
  const { message, usage, timing } = await time('applyChangesViaSpeculativeDecoding', () =>
    model.getApi().streamChatCompletions('', [], messages, () => {}, originalFileCodeBlock),
  )
  trackUsage('applyChangesViaSpeculativeDecoding', usage, model)

  debug_block(
    'diff new content and original',
    Diff.diffLines(originalFileCodeBlock, message.content),
  )
  let content = parseFromPossibleBackticks(message.content)
  debug_block('New file contents', content)
  debug_block('applyChangesViaSpeculativeDecoding', { usage, timing })

  // Special case newline at end of file since the model may not include it.
  if (originalFileContents.endsWith('\n') && !content.endsWith('\n')) {
    content += '\n'
  }

  const diffLines = Diff.diffLines(originalFileContents, content)
  const rewrites = diffChangesToRewrites(diffLines)

  return rewrites.map((rewrite) => ({
    ...rewrite,
    type: 'editFile',
    filePath: firstChange.path,
  }))
}

/**
 * Apply a list of code replacements for generate_with_stream_v3.
 */
export function applyCodeReplacements(replacements: CodeReplacement[]): RewriteWithPath[] {
  const replacementsByPath = new Map<string, CodeReplacement[]>()
  for (const replacement of replacements) {
    const existing = replacementsByPath.get(replacement.location) || []
    existing.push(replacement)
    replacementsByPath.set(replacement.location, existing)
  }

  const allRewrites: RewriteWithPath[] = []
  for (const [path, fileReplacements] of Array.from(replacementsByPath.entries())) {
    let content = existsSync(path) ? readFileSync(path, 'utf8') : ''
    const originalContent = content

    for (const replacement of fileReplacements) {
      if (replacement.find === '') {
        content = replacement.replace
      } else if (content.includes(replacement.find)) {
        content = content.replace(replacement.find, replacement.replace)
      } else {
        error_block('Failed to apply replacement', {
          path,
          replacement,
        })
      }
    }

    if (originalContent !== content) {
      const diffLines = Diff.diffLines(originalContent, content)
      const rewrites = diffChangesToRewrites(diffLines)
      allRewrites.push(
        ...rewrites.map(
          (rewrite) =>
            ({
              ...rewrite,
              type: originalContent === '' ? 'createFile' : 'editFile',
              filePath: path,
            }) as RewriteWithPath,
        ),
      )
    }
  }
  return allRewrites
}
